import { createRouter, createWebHistory } from 'vue-router'
import store from '../store'

/**
 * Per-SOP module mapping for /tools/:sopName/* routes.
 * Add an entry here when a new SOP becomes a grantable module.
 * Sub-SOPs called internally (audit-social-content, render-linkedin-image,
 * rewrite-in-tov) are NOT listed — they aren't directly accessible by users.
 */
const SOP_MODULE_MAP = {
  'brief-to-linkedin-campaign': 'linkedin-post-generator',
  'brief-to-estimation': 'estimation'
}

const routes = [
  {
    path: '/login',
    name: 'Login',
    component: () => import('../views/Login.vue'),
    meta: { guest: true }
  },
  {
    // Gateway — landing page with tiles for the user's granted modules.
    path: '/',
    name: 'Gateway',
    component: () => import('../views/Gateway.vue'),
    meta: { requiresAuth: true }
  },
  {
    // Project Setup module home (was '/' Dashboard).
    path: '/project-setup',
    name: 'ProjectSetup',
    component: () => import('../views/Dashboard.vue'),
    meta: { requiresAuth: true, requiresModule: 'project-setup' }
  },
  {
    path: '/phases/:phaseId',
    name: 'PhaseDetail',
    component: () => import('../views/PhaseDetail.vue'),
    meta: { requiresAuth: true, requiresModule: 'project-setup' }
  },
  {
    path: '/metrics',
    name: 'Metrics',
    component: () => import('../views/Metrics.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: () => import('../views/AdminUsers.vue'),
    meta: { requiresAuth: true, requiresAdmin: true }
  },
  {
    // SOP-driven tool routes. The :sopName segment selects which SOP to load;
    // module access is resolved per-SOP via SOP_MODULE_MAP in the guard below.
    // AgentToolRoot decides between the per-SOP dashboard (root URL, SOP
    // opted in via frontmatter ui.dashboard) and the AgentChat shell
    // (session URLs, phase deep-links, SOPs without a dashboard).
    path: '/tools/:sopName/:sessionSegment(sessions)?/:sessionId?',
    name: 'AgentChat',
    component: () => import('../views/AgentToolRoot.vue'),
    meta: { requiresAuth: true },
    props: true
  },
  {
    path: '/connect-services',
    name: 'ConnectServices',
    component: () => import('../views/ConnectServices.vue'),
    meta: { requiresAuth: true }
  },
  {
    path: '/oauth/callback',
    name: 'OAuthCallback',
    component: () => import('../views/OAuthCallback.vue')
  },
  {
    path: '/interview',
    name: 'Interview',
    component: () => import('../views/Interview.vue')
  },
  {
    path: '/interview/admin',
    name: 'InterviewAdmin',
    component: () => import('../views/InterviewAdmin.vue'),
    meta: { requiresAuth: true, requiresModule: 'discovery-interviews' }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: () => import('../views/NotFound.vue')
  }
]

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes
})

router.beforeEach(async (to, from, next) => {
  const isAuthenticated = store.getters['auth/isAuthenticated']
  const token = localStorage.getItem('token')

  // Restore session if token present but user not loaded.
  if (token && !isAuthenticated) {
    try {
      await store.dispatch('auth/fetchUser')
    } catch (error) {
      localStorage.removeItem('token')
    }
  }

  const isAuth = store.getters['auth/isAuthenticated']

  // Guest pages
  if (to.meta.guest && isAuth) return next('/')
  if (to.meta.requiresAuth && !isAuth) {
    if (to.path === '/login' && (to.query.auth || to.query.error)) return next()
    return next('/login')
  }
  if (!to.meta.requiresAuth) return next()

  // Admin-only pages (admin or super both pass)
  if (to.meta.requiresAdmin && !store.getters['auth/isAdmin']) {
    return next('/')
  }

  // Resolve module requirement — direct meta or SOP-name-based for /tools/*.
  let requiredModule = to.meta.requiresModule
  if (!requiredModule && to.name === 'AgentChat') {
    requiredModule = SOP_MODULE_MAP[to.params.sopName] || null
    // Unknown SOP name → reject. Without this, any authed user (including
    // pending) could navigate to /tools/anything-goes and reach the
    // AgentChat shell. The backend would then reject the actual API calls,
    // but the URL itself shouldn't be reachable.
    if (!requiredModule) {
      return next('/?error=unknown_tool')
    }
  }

  if (requiredModule) {
    const userModules = store.getters['auth/userModules']
    if (!userModules.includes(requiredModule)) {
      return next('/?error=module_not_granted')
    }

    // Project Setup prerequisite: Basecamp must be connected. Replaces the old
    // global BC gate that used to block ALL authed routes.
    if (requiredModule === 'project-setup') {
      const isBcConnected = store.getters['auth/isBasecampConnected']
      if (!isBcConnected && to.name !== 'ConnectServices') {
        return next('/connect-services')
      }
    }
  }

  // Don't trap users on /connect-services if they no longer need it.
  if (to.name === 'ConnectServices' && store.getters['auth/isBasecampConnected']) {
    return next('/')
  }

  next()
})

export default router
